/** SVG visuals + live console PNGs for the marketing landing. */

export const LIVE_SHOTS = {
  chat: "/screenshots/console-chat.png",
  logs: "/screenshots/console-logs.png",
  kb: "/screenshots/console-kb.png",
  integrations: "/screenshots/console-integrations.png",
  dashboard: "/screenshots/console-dashboard.png",
  incidents: "/screenshots/console-incidents.png",
  hero: "/screenshots/console-chat.png",
};

export function ScreenshotFrame({ src, alt, compact = false, className = "" }) {
  return (
    <div className={`lp-shot-frame ${compact ? "compact" : ""} ${className}`.trim()}>
      <div className="lp-shot-chrome">
        <span className="lp-mock-dot red" />
        <span className="lp-mock-dot amber" />
        <span className="lp-mock-dot green" />
        <span className="lp-mock-url">kt.copilot.wecrew.in</span>
      </div>
      <img src={src} alt={alt} className="lp-shot-img" loading="lazy" decoding="async" />
    </div>
  );
}

export function ConsoleGallery() {
  const items = [
    { src: LIVE_SHOTS.chat, label: "L2 Chat", page: "chat" },
    { src: LIVE_SHOTS.logs, label: "Observability", page: "logs" },
    { src: LIVE_SHOTS.kb, label: "Knowledge Bases", page: "kb" },
    { src: LIVE_SHOTS.integrations, label: "Integrations", page: "integrations" },
    { src: LIVE_SHOTS.dashboard, label: "Dashboard", page: "dashboard" },
  ];
  return (
    <div className="lp-gallery">
      {items.map((item) => (
        <figure key={item.label} className="lp-gallery-item">
          <ScreenshotFrame src={item.src} alt={`Wecrew Copilot ${item.label} — live console`} compact />
          <figcaption className="lp-gallery-cap">{item.label}</figcaption>
        </figure>
      ))}
    </div>
  );
}

export function OrbitVisual() {
  return (
    <div className="lp-orbit" aria-hidden>
      <svg viewBox="0 0 420 420" fill="none" className="lp-orbit-svg">
        <defs>
          <linearGradient id="lp-grad-o" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff5b2e" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#ff5b2e" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lp-grad-b" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2b4cff" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#2b4cff" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="lp-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ff5b2e" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#2b4cff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="210" cy="210" r="175" stroke="url(#lp-grad-o)" strokeWidth="1.5" strokeDasharray="6 10" />
        <circle cx="210" cy="210" r="135" stroke="url(#lp-grad-b)" strokeWidth="1" />
        <circle cx="210" cy="210" r="95" stroke="rgba(15,122,85,0.25)" strokeWidth="1" strokeDasharray="3 8" />
        <circle cx="210" cy="210" r="55" fill="url(#lp-glow)" />
        {[0, 72, 144, 216, 288].map((deg, i) => {
          const r = 135;
          const x = 210 + r * Math.cos((deg * Math.PI) / 180);
          const y = 210 + r * Math.sin((deg * Math.PI) / 180);
          const colors = ["#ff5b2e", "#2b4cff", "#0f7a55", "#8a6d2e", "#7c3aed"];
          return (
            <g key={deg}>
              <line x1="210" y1="210" x2={x} y2={y} stroke={colors[i]} strokeOpacity="0.12" strokeWidth="1" />
              <circle cx={x} cy={y} r="5" fill={colors[i]} opacity="0.85">
                <animate attributeName="opacity" values="0.5;1;0.5" dur={`${3 + i * 0.4}s`} repeatCount="indefinite" />
              </circle>
            </g>
          );
        })}
      </svg>
      <div className="lp-orbit-core" />
      <div className="lp-orbit-glyph">
        W<em>crew</em>
      </div>
      {[
        ["lp-oc1", "RAG", "orange"],
        ["lp-oc2", "K8s", "blue"],
        ["lp-oc3", "ITSM", "green"],
        ["lp-oc4", "Ops", "gold"],
        ["lp-oc5", "Live", "plum"],
      ].map(([cls, label, tone]) => (
        <span key={cls} className={`lp-orbit-chip ${cls} ${tone}`}>
          <i className="lp-oc-dot" /> {label}
        </span>
      ))}
    </div>
  );
}

export function HeroConsoleMock() {
  return (
    <ScreenshotFrame
      src={LIVE_SHOTS.hero}
      alt="Wecrew Copilot L2 Chat — live production console"
      className="lp-hero-mock"
    />
  );
}

export function CapabilityIcon({ tone }) {
  const palettes = {
    orange: ["#ff5b2e", "#ffb185"],
    blue: ["#2b4cff", "#93c5fd"],
    green: ["#0f7a55", "#86efac"],
  };
  const [a, b] = palettes[tone] || palettes.orange;
  return (
    <div className={`lp-cap-icon ${tone}`} aria-hidden>
      <svg viewBox="0 0 64 64" fill="none">
        <rect width="64" height="64" rx="16" fill={`url(#cap-${tone})`} />
        <defs>
          <linearGradient id={`cap-${tone}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={a} />
            <stop offset="100%" stopColor={b} />
          </linearGradient>
        </defs>
        {tone === "orange" && (
          <>
            <rect x="14" y="18" width="36" height="8" rx="4" fill="rgba(255,255,255,0.9)" />
            <rect x="14" y="30" width="28" height="6" rx="3" fill="rgba(255,255,255,0.55)" />
            <rect x="14" y="40" width="32" height="6" rx="3" fill="rgba(255,255,255,0.55)" />
          </>
        )}
        {tone === "blue" && (
          <>
            <path d="M12 44 L20 28 L28 36 L38 22 L52 44 Z" fill="rgba(255,255,255,0.85)" />
            <circle cx="46" cy="20" r="4" fill="rgba(255,255,255,0.6)" />
          </>
        )}
        {tone === "green" && (
          <>
            <circle cx="32" cy="32" r="14" stroke="rgba(255,255,255,0.9)" strokeWidth="3" fill="none" />
            <path d="M26 32 L30 36 L38 26" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          </>
        )}
      </svg>
    </div>
  );
}

export function BentoVisual({ variant }) {
  const map = {
    chat: LIVE_SHOTS.chat,
    logs: LIVE_SHOTS.logs,
    kb: LIVE_SHOTS.kb,
    integrations: LIVE_SHOTS.integrations,
    itsm: LIVE_SHOTS.incidents,
  };
  const src = map[variant];
  if (src) {
    return (
      <ScreenshotFrame
        src={src}
        alt={`Wecrew Copilot ${variant} screen`}
        compact
        className="lp-bento-shot"
      />
    );
  }
  return null;
}

export function ShowcaseScreenshot({ tab }) {
  const map = {
    chat: LIVE_SHOTS.chat,
    ops: LIVE_SHOTS.logs,
    itsm: LIVE_SHOTS.incidents,
  };
  const labels = { chat: "L2 Chat", ops: "Observability", itsm: "ITSM Incidents" };
  return (
    <ScreenshotFrame
      src={map[tab] || LIVE_SHOTS.chat}
      alt={`Wecrew Copilot ${labels[tab] || tab} — live console screenshot`}
    />
  );
}

export function ProviderStrip() {
  const items = [
    { glyph: "A", tone: "orange", name: "Copilot", tag: "AI Engine" },
    { glyph: "K", tone: "green", name: "Kubernetes", tag: "Runbooks" },
    { glyph: "S", tone: "plum", name: "ServiceNow", tag: "ITSM" },
    { glyph: "R", tone: "blue", name: "RAG", tag: "Knowledge" },
  ];
  return (
    <section className="lp-providers">
      <p className="lp-num">Integrations</p>
      <div className="lp-prov-grid">
        {items.map((p) => (
          <article key={p.name} className={`lp-prov-card ${p.tone}`}>
            <div className="lp-prov-icon">{p.glyph}</div>
            <div className="lp-prov-name">{p.name}</div>
            <div className="lp-prov-tag">{p.tag}</div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function ArchitectureDiagram() {
  return (
    <div className="lp-arch" aria-hidden>
      <svg viewBox="0 0 520 200" className="lp-arch-svg">
        <defs>
          <linearGradient id="arch-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ff5b2e" />
            <stop offset="50%" stopColor="#2b4cff" />
            <stop offset="100%" stopColor="#0f7a55" />
          </linearGradient>
        </defs>
        {[
          [40, 100, "SOPs"],
          [160, 100, "Chroma"],
          [280, 100, "Copilot"],
          [400, 100, "L2 Team"],
        ].map(([x, y, label], i) => (
          <g key={label}>
            <rect x={Number(x) - 44} y={Number(y) - 28} width="88" height="56" rx="14" fill="#faf7f0" stroke="rgba(14,17,22,0.12)" />
            <text x={x} y={Number(y) + 5} textAnchor="middle" fontFamily="Instrument Serif, serif" fontSize="15" fill="#0e1116">
              {label}
            </text>
            {i < 3 && (
              <path
                d={`M ${Number(x) + 44} ${y} L ${Number(x) + 72} ${y}`}
                stroke="url(#arch-line)"
                strokeWidth="2"
                markerEnd="url(#arrow)"
              />
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}
